import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import ContactButton from './components/ContactButton';
import AIIcon from './assets/icons/AI.svg';
import WebIcon from './assets/icons/web.svg';
import MobileIcon from './assets/icons/mobile.svg';
import ChatbotIcon from './assets/icons/chatbot.svg';
import AutomationIcon from './assets/icons/automation.svg';
import IntegrationIcon from './assets/icons/integration.svg';
import ConsultingIcon from './assets/icons/consulting.svg';


const Card = ({ title, description, isActive, icon, showButton = true }) => (
  <motion.div
    className={`w-[90%] sm:w-full h-full rounded-3xl px-12 py-4 m-auto flex-col transition-all duration-400 ${isActive ? 'bg-[#5C3DFF]' : 'bg-[#3A3357]'
      }`}
    animate={{
      scale: isActive ? 1 : 0.9,
      opacity: isActive ? 1 : 0.7,
    }}
    transition={{ duration: 0.5 }}
  >
    <div className="h-[40%] flex items-center justify-center">
      {icon}
    </div>
    <div className="">
      <div className="mt-8">
        <h3 className="text-[#C6FF00] text-2xl font-bold mb-2 uppercase tracking-wide">
          {title}
        </h3>
        <p className="text-white text-lg">
          {description}
        </p>
      </div>
      {showButton && (
        <a
          href="/forms"
          target="_blank"
          rel="noopener noreferrer"
        >
          <button
            className="bg-[#C6FF00] text-[#1C1B2D] px-8 py-4 rounded-full font-medium 
                    hover:bg-[#B3E600] transition-colors duration-300 text-lg"
          >
            Entrar em contato
          </button>
        </a>
      )}
    </div>
  </motion.div>
);

const FeatureCards = ({ id }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const cards = [
    {
      title: "Agentes de IA",
      description: "Modelos de inteligência artificial personalizados para análise, automação e tomada de decisão.",
      icon: <img src={AIIcon} alt="AI Icon" className="h-full" />
    },
    {
      title: "Sistemas Web",
      description: "Plataformas web escaláveis e responsivas utilizando as mais modernas tecnologias do mercado.",
      icon: <img src={WebIcon} alt="Web Icon" className="h-full" />
    },
    {
      title: "Aplicativos Mobile",
      description: "Desenvolvimento de aplicativos nativos e multiplataforma com foco em performance e experiência do usuário.",
      icon: <img src={MobileIcon} alt="Mobile Icon" className="h-full" />
    },
    {
      title: "Chatbot Inteligente",
      description: "Automatize o atendimento com bots que respondem, interagem e vendem 24/7.",
      icon: <img src={ChatbotIcon} alt="Mobile Icon" className="h-full" />
    },
    {
      title: "Automação de Processos",
      description: "Elimine tarefas manuais e ganhe produtividade com fluxos automatizados.",
      icon: <img src={AutomationIcon} alt="Mobile Icon" className="h-full" />
    },
    {
      title: "integração com Plataformas",
      description: "Conectamos seu software a ERP, CRM, redes sociais e outras ferramentas.",
      icon: <img src={IntegrationIcon} alt="Mobile Icon" className="h-full" />
    },
    {
      title: "Consultoria de TI",
      description: "Estratégias e tecnologia sob medida para impulsionar a inovação e eficiência do seu negócio.",
      icon: <img src={ConsultingIcon} alt="Mobile Icon" className="h-full" />
    }
  ];

  const nextCard = () => {
    setCurrentIndex((prev) => (prev + 1) % cards.length);
  };

  const prevCard = () => {
    setCurrentIndex((prev) => (prev - 1 + cards.length) % cards.length);
  };

  return (
    <section className="bg-[#1C1B2D] w-full pt-4 pb-8" id={id}>
      <div className="w-full max-w-[1400px] mx-auto lg:my-12">
        {/* Título e Subtítulo */}
        <div className="text-left px-4 mb-8 text-center">
          <h2 className="text-[#C6FF00] text-3xl sm:text-4xl font-bold font-montserrat mb-2">
            Soluções
          </h2>
          <p className="text-white text-lg font-montserrat">
            Desenvolvemos soluções tecnológicas sob medida, com expertise em
            dezenas de projetos que transformaram negócios.
          </p>
        </div>

        {/* Cards Container */}
        <div className="relative w-full h-[52vh]">
          {/* Botão Anterior */}
          <button
            onClick={prevCard}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-30 w-12 h-12 sm:bg-white sm:bg-opacity-20 rounded-full 
              flex items-center justify-center hover:bg-opacity-30 transition-all
              hover:scale-110 focus:scale-110"
            aria-label="Card anterior"
          >
            <FaChevronLeft className="text-white text-xl" />
          </button>

          <div className="w-full h-full overflow-hidden relative">
            {cards.map((card, index) => {
              const position = (index - currentIndex + cards.length) % cards.length;
              const isActive = position === 0;

              // Definindo valores de posicionamento horizontal explícitos
              let leftPosition = "50%"; // Valor padrão - centralizado
              let transformX = "translateX(-50%)"; // Centraliza o elemento
              let opacity = 0;
              let scale = 0.9;

              if (position === 0) { // Card ativo (centro)
                leftPosition = "50%";
                transformX = "translateX(-50%)";
                opacity = 1;
                scale = 1;
              }
              else if (position === 1) { // Primeiro card à direita
                leftPosition = "75%";
                transformX = "translateX(-50%)";
                opacity = 0.4;
                scale = 0.95;
              }
              else if (position === cards.length - 1) { // Primeiro card à esquerda
                leftPosition = "25%";
                transformX = "translateX(-50%)";
                opacity = 0.4;
                scale = 0.95;
              }
              else if (position === 2) { // Segundo card à direita
                leftPosition = "95%";
                transformX = "translateX(-75%)";
                opacity = 0.05;
                scale = 0.8;
              }
              else if (position === cards.length - 2) { // Segundo card à esquerda
                leftPosition = "5%";
                transformX = "translateX(-25%)";
                opacity = 0.05;
                scale = 0.8;
              }

              // Só mostrar em desktop
              const displayClass = position === 0 ? "" : "hidden sm:block";

              // Estilo inline para posicionamento preciso
              const cardStyle = {
                left: leftPosition,
                transform: `${transformX} scale(${scale})`,
                opacity: opacity,
              };

              return (
                <div
                  key={index}
                  className={`absolute top-0 ${displayClass} h-full w-full sm:w-auto transition-all duration-300`}
                  style={cardStyle}
                >
                  <div className="m-auto h-full w-full sm:w-64 md:w-70 lg:w-[20rem] xl:w-96">
                    <Card {...card} isActive={isActive} showButton={false} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Botão Próximo */}
          <button
            onClick={nextCard}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-30 w-12 h-12 sm:bg-white sm:bg-opacity-20 rounded-full 
              flex items-center justify-center hover:bg-opacity-30 transition-all
              hover:scale-110"
            aria-label="Próximo card"
          >
            <FaChevronRight className="text-white text-xl" />
          </button>
        </div>

        {/* Botão de Contato */}
        <div className="text-center mt-8">
          <ContactButton onClick={() => window.location.href = '#contact'} />
        </div>
      </div>
    </section>
  );
};

export default FeatureCards;
